import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { CreditNoteRow, PortalApi, ReceiptRow, Statement } from './portalApi';
import { portalErrorMessage } from './portalHttp';
import { usePortalList } from './usePortalList';
import { saveBlob } from '../utils/saveBlob';
import { formatMoney, humanizeEnum } from '../utils/format';
import { formatDate } from '../utils/dateUtils';

// ---- P1-B customer portal: receipts, credit notes and statement ----
const Pager: React.FC<{ list: ReturnType<typeof usePortalList<any>> }> = ({ list }) => (
  <TablePagination component="div" count={list.data?.total ?? 0} page={list.page} rowsPerPage={list.size}
    rowsPerPageOptions={[10, 20, 50]} onPageChange={(_, p) => list.setPage(p)}
    onRowsPerPageChange={e => { list.setSize(Number(e.target.value)); list.setPage(0); }} />
);

const usePdf = (fetch: (id: string) => Promise<Blob>) => {
  const [error, setError] = useState<string | null>(null);
  const download = async (id: string, number: string) => {
    setError(null);
    try { saveBlob(await fetch(id), `${number}.pdf`); } catch (e) { setError(portalErrorMessage(e, 'Could not download the PDF')); }
  };
  return { error, download };
};

export const PortalReceipts: React.FC = () => {
  const list = usePortalList<ReceiptRow>((p, s) => PortalApi.receipts(p, s));
  const pdf = usePdf(PortalApi.receiptPdf);
  return (
    <Box>
      <Typography variant="h5" gutterBottom>Receipts</Typography>
      {(list.error || pdf.error) && <Alert severity="error" sx={{ mb: 2 }}>{list.error || pdf.error}</Alert>}
      {list.loading && !list.data ? <CircularProgress /> : (
        <Paper><TableContainer><Table size="small">
          <TableHead><TableRow>
            <TableCell>Receipt</TableCell><TableCell>Date</TableCell><TableCell>Invoice</TableCell><TableCell>Method</TableCell>
            <TableCell>Reference</TableCell><TableCell align="right">Amount</TableCell><TableCell />
          </TableRow></TableHead>
          <TableBody>
            {(list.data?.rows ?? []).length === 0 && <TableRow><TableCell colSpan={7} align="center">No receipts.</TableCell></TableRow>}
            {(list.data?.rows ?? []).map(r => (
              <TableRow key={r.id}>
                <TableCell>{r.number}</TableCell><TableCell>{formatDate(r.date)}</TableCell><TableCell>{r.invoiceNumber ?? 'On account'}</TableCell>
                <TableCell>{r.method ? humanizeEnum(r.method) : '-'}</TableCell><TableCell>{r.reference ?? '-'}</TableCell>
                <TableCell align="right">{formatMoney(r.amount)}</TableCell>
                <TableCell align="right"><Button size="small" onClick={() => pdf.download(r.id, r.number)}>PDF</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></TableContainer><Pager list={list} /></Paper>
      )}
    </Box>
  );
};

export const PortalCreditNotes: React.FC = () => {
  const list = usePortalList<CreditNoteRow>((p, s) => PortalApi.creditNotes(p, s));
  const pdf = usePdf(PortalApi.creditNotePdf);
  return (
    <Box>
      <Typography variant="h5" gutterBottom>Credit notes</Typography>
      {(list.error || pdf.error) && <Alert severity="error" sx={{ mb: 2 }}>{list.error || pdf.error}</Alert>}
      {list.loading && !list.data ? <CircularProgress /> : (
        <Paper><TableContainer><Table size="small">
          <TableHead><TableRow>
            <TableCell>Credit note</TableCell><TableCell>Date</TableCell><TableCell>Invoice</TableCell>
            <TableCell align="right">Total</TableCell><TableCell>Status</TableCell><TableCell />
          </TableRow></TableHead>
          <TableBody>
            {(list.data?.rows ?? []).length === 0 && <TableRow><TableCell colSpan={6} align="center">No credit notes.</TableCell></TableRow>}
            {(list.data?.rows ?? []).map(r => (
              <TableRow key={r.id}>
                <TableCell>{r.number}</TableCell><TableCell>{formatDate(r.date)}</TableCell><TableCell>{r.invoiceNumber ?? '-'}</TableCell>
                <TableCell align="right">{formatMoney(r.total)}</TableCell>
                <TableCell><Chip size="small" label={humanizeEnum(r.status)} color={r.status === 'POSTED' ? 'success' : 'default'} /></TableCell>
                <TableCell align="right"><Button size="small" onClick={() => pdf.download(r.id, r.number)}>PDF</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></TableContainer><Pager list={list} /></Paper>
      )}
    </Box>
  );
};

export const PortalStatement: React.FC = () => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState<Statement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try { setData(await PortalApi.statement(from || undefined, to || undefined)); }
    catch (e) { setError(portalErrorMessage(e, 'Could not load the statement')); }
    finally { setLoading(false); }
  };
  // The first load uses the server default range (last 6 months).
  React.useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    try { saveBlob(await PortalApi.statementPdf(from || undefined, to || undefined), 'statement.pdf'); }
    catch (e) { setError(portalErrorMessage(e, 'Could not download the statement')); }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Statement</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField size="small" type="date" label="From" value={from} InputLabelProps={{ shrink: true }}
          sx={{ flex: '1 1 160px', maxWidth: 220 }} onChange={e => setFrom(e.target.value)} />
        <TextField size="small" type="date" label="To" value={to} InputLabelProps={{ shrink: true }}
          sx={{ flex: '1 1 160px', maxWidth: 220 }} onChange={e => setTo(e.target.value)} />
        <Button variant="contained" onClick={load} disabled={loading}>Show</Button>
        <Button variant="outlined" onClick={download}>Download PDF</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !data ? <CircularProgress /> : data && (
        <Paper><TableContainer><Table size="small">
          <TableHead><TableRow>
            <TableCell>Date</TableCell><TableCell>Document</TableCell><TableCell>Reference</TableCell>
            <TableCell align="right">Debit</TableCell><TableCell align="right">Credit</TableCell><TableCell align="right">Balance</TableCell>
          </TableRow></TableHead>
          <TableBody>
            <TableRow><TableCell colSpan={5}>Opening balance</TableCell><TableCell align="right">{formatMoney(data.openingBalance)}</TableCell></TableRow>
            {data.rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{formatDate(r.date)}</TableCell><TableCell>{humanizeEnum(r.documentType)}</TableCell><TableCell>{r.reference}</TableCell>
                <TableCell align="right">{r.debit ? formatMoney(r.debit) : ''}</TableCell>
                <TableCell align="right">{r.credit ? formatMoney(r.credit) : ''}</TableCell>
                <TableCell align="right">{formatMoney(r.balance)}</TableCell>
              </TableRow>
            ))}
            <TableRow><TableCell colSpan={5}><b>Closing balance</b></TableCell><TableCell align="right"><b>{formatMoney(data.closingBalance)}</b></TableCell></TableRow>
          </TableBody>
        </Table></TableContainer></Paper>
      )}
    </Box>
  );
};
// ---- end P1-B ----
